import React, { useCallback } from "react";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { VendorInput } from "../../../input/VendorInput/VendorInput";
import { ConditionSelectFilter } from "../../Condition/ConditionSelectFilter";
import { FilterMode } from "../../Condition/types";

import { FaiFilterSection } from "../FaiFilter/FaiFilterSection";
import { InstallAgeSection } from "../InstallAgeFilter/InstallAgeSection";
import { VendorInstallsCountSection } from "./VendorInstallsCountSection";
import { FilterRange } from "../../Ranges/types";
import CollectionSelect from "../../../CollectionSelect/CollectionSelect";

interface InclusionSectionProps {
  condition: FilterMode;
  selectedVendorIds: number[];
  onConditionChange: (condition: FilterMode) => void;
  onVendorSelect: (vendorIds: number[]) => void;
  selectedFaiIds?: string[];
  onFaiSelect?: (
    selectedDepartmentIds: string[],
    selectedRoleIds: string[]
  ) => void;
  installAgeMonths?: { min?: number | null; max?: number | null };
  onInstallAgeChange?: (installAge: {
    min?: number | null;
    max?: number | null;
  }) => void;
  productCount?: FilterRange;
  onProductCountChange?: (productCount: FilterRange) => void;
}

export const InclusionSection: React.FC<InclusionSectionProps> = React.memo(
  ({
    condition,
    selectedVendorIds,
    onConditionChange,
    onVendorSelect,
    selectedFaiIds = [],
    onFaiSelect,
    installAgeMonths,
    onInstallAgeChange,
    productCount,
    onProductCountChange,
  }) => {
    const handleFaiSelect = useCallback(
      (selectedDepartmentIds: string[], selectedRoleIds: string[]) => {
        if (onFaiSelect) {
          onFaiSelect(selectedDepartmentIds, selectedRoleIds);
        }
      },
      [onFaiSelect]
    );

    return (
      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 2,
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 2,
        }}
      >
        <Stack direction="row" alignItems="center" mb={1}>
          <Typography variant="body1">
            <strong>Include</strong> companies using
          </Typography>
          <ConditionSelectFilter
            id="vendor-condition"
            value={condition}
            onChange={onConditionChange}
          />
          <Typography variant="body1">of the following vendors</Typography>
        </Stack>

        <Stack direction="column" gap={1}>
          <VendorInput
            onVendorsSelect={(value) =>
              onVendorSelect(value.map((v) => Number(v.value)))
            }
            selectedVendorIds={selectedVendorIds}
          />

          <CollectionSelect
            placeholder="Search Collections"
            type="vendor"
            onSelect={({ ids }) =>
              onVendorSelect([
                ...selectedVendorIds,
                ...(Array.isArray(ids) ? ids : [])
                  .map((id) => Number(id))
                  .filter((id) => Number.isFinite(id))
                  .filter((id) => !selectedVendorIds.includes(id)),
              ])
            }
          />
        </Stack>

        <VendorInstallsCountSection
          disabled={selectedVendorIds.length === 0}
          productCount={productCount}
          onProductCountChange={onProductCountChange || (() => {})}
          id="vendor-installs-count-filter"
          sx={{ mt: 1 }}
        />

        <FaiFilterSection
          disabled={selectedVendorIds.length === 0}
          selectedIds={selectedFaiIds}
          onFaiSelect={handleFaiSelect}
          id="vendor-fai-filter"
        />

        <InstallAgeSection
          disabled={selectedVendorIds.length === 0}
          value={installAgeMonths || { min: undefined, max: undefined }}
          onChange={onInstallAgeChange || (() => {})}
          filterHasSelection={selectedVendorIds.length > 0}
        />
      </Paper>
    );
  }
);
